/**
 * AnalyticsFunnel — /admin/analytics/funnel (Wave 5-Late Analytics ANCHOR).
 *
 * Executive admin discipline inherited from the approved Marketplace
 * anchor + Beta anchor v2:
 *   • AdminLayout wrap (previously missing — gap closed for consistency
 *     with every other admin surface).
 *   • Section primitive with `--ds-color-admin-border` hairline chrome
 *     + `box-shadow: 0 1px 0 0 admin-border`, header `px-4 py-2.5`
 *     with icon + Heading.
 *   • Inputs / selects / buttons on `--ds-radius-input` (3px) +
 *     `--ds-color-input-border`, dense `px-2.5 py-1.5`.
 *   • KPI stat cards use hairline chrome + `tabular-nums` on values
 *     (critical for an analytics surface).
 *   • recharts palette rewired to the DS admin palette — bars in a
 *     uniform `--ds-color-accent-strong`, axis ticks in
 *     `--ds-color-ink-subtle` / `--ds-color-ink-muted`, grid in
 *     `--ds-color-admin-border`, tooltip in surface + admin-border.
 *     No saturated purple ramp, no gradient fills, no cyan overlay.
 *   • Cohort/source breakdown horizontal bars use solid tokenised
 *     fills — the previous inline gradient fill is removed
 *     (unblocks `check_inline_gradient.sh` further).
 *   • Event volume table uses dense hairline rows with `tabular-nums`
 *     on counts + last-row guard.
 *   • Error banner restyled to `--ds-color-danger*` tokens (was
 *     rgba(252,165,165,…) hex literal).
 *
 * Business logic (AbortController fetch, combined dashboard endpoint,
 * ignored CanceledError, stage list, breakdown computation) preserved
 * BYTE-EXACT. All testids unchanged.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Filter, Layers, ListTree, Loader2, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, formatApiError } from "@/api/client";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Heading } from "@/design-system/components/Heading";
import { GhostButton, Section, SelectInput, Stat } from "@/admin/_primitives";

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror Marketplace / Beta anchors).          */
/* ------------------------------------------------------------------ */




/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AnalyticsFunnel() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [cohort, setCohort] = useState("");
  const [loadErr, setLoadErr] = useState("");
  // AbortController so a fast filter-change cancels in-flight requests
  // instead of letting them race and stomp on each other.
  const inflightRef = useRef(null);

  async function load() {
    setLoading(true);
    if (inflightRef.current) inflightRef.current.abort();
    const ctrl = new AbortController();
    inflightRef.current = ctrl;
    try {
      // V1.1-Beta hot-fix · single combined endpoint. Avoids the "one of two
      // parallel fetches transiently fails" failure mode altogether.
      const { data: payload } = await api.get("/admin/analytics/dashboard", {
        params: { days, cohort: cohort || undefined },
        signal: ctrl.signal,
      });
      setData(payload.funnel || null);
      setSummary(payload.events_summary || []);
      setBreakdown(payload.breakdown || null);
      setLoadErr("");
    } catch (err) {
      // Abort errors (from a newer in-flight request) MUST be ignored —
      // they don't represent a real failure.
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || ctrl.signal.aborted) {
        return;
      }
      const msg = formatApiError(err);
      setLoadErr(msg);
      toast.error(`Analytics: ${msg}`);
    } finally {
      if (inflightRef.current === ctrl) {
        inflightRef.current = null;
        setLoading(false);
      }
    }
  }
  useEffect(() => {
    load();
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort();
        inflightRef.current = null;
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [days, cohort]);

  const stages = data?.stages || [];
  const chartData = stages.map((s) => ({ name: s.label, count: s.count }));

  const filterBadge = (
    <GhostButton
      onClick={load}
      data-testid="admin-analytics-funnel-refresh"
      className="!py-1 !text-[11px]"
    >
      <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
      Refresh
    </GhostButton>
  );

  return (
    <AdminLayout
      title="Signup → Activation funnel"
      subtitle={`In-house analytics. Unique users (or sessions for anon) per stage in the last ${days} days.`}
    >
      <div data-testid="admin-analytics-funnel-page">
        <div className="mb-3 max-w-3xl">
          <AdminPageHelp
            title="Analytics Funnel"
            purpose="Signup → activation → conversion funnel with per-step drop-off analysis."
            configures="Nothing — read-only. Filter by cohort, source, and time window."
            impact="No user-facing impact. Used for product and marketing prioritization."
            safety="Read-only. Metrics update on schedule from platform events."
          />
        </div>

        <Section title="Filter" icon={Filter} badge={filterBadge} testid="admin-analytics-funnel-filters">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Time window
              </span>
              <SelectInput
                data-testid="admin-analytics-funnel-days"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
              >
                {[7, 14, 30, 60, 90, 180, 365].map((d) => (
                  <option key={d} value={d}>Last {d} days</option>
                ))}
              </SelectInput>
            </label>
            <label className="block">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Cohort
              </span>
              <SelectInput
                data-testid="admin-analytics-funnel-cohort"
                value={cohort}
                onChange={(e) => setCohort(e.target.value)}
              >
                <option value="">All cohorts</option>
                {(data?.available_cohorts || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </SelectInput>
            </label>
          </div>
        </Section>

        <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="Total events" value={data?.total_events_in_window ?? 0} testId="admin-analytics-stat-events" />
          <Stat label="Funnel entry (landing views)" value={stages[0]?.count ?? 0} testId="admin-analytics-stat-entry" />
          <Stat
            label="Overall conversion %"
            value={data ? `${data.overall_conversion_pct}%` : "—"}
            testId="admin-analytics-stat-conversion"
            highlight
          />
        </div>

        {loadErr && (
          <div
            data-testid="admin-analytics-load-error"
            className="mb-3 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
            style={{
              borderColor: "var(--ds-color-danger)",
              color: "var(--ds-color-danger)",
              background: "var(--ds-color-danger-soft)",
            }}
          >
            Couldn't load analytics — {loadErr}
          </div>
        )}

        <Section
          title="Funnel — unique reach per stage"
          icon={BarChart3}
          testid="admin-analytics-funnel-chart"
          bodyClassName="p-4"
        >
          {loading && !data && (
            <div
              className="py-10 text-center text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              <Loader2 className="w-4 h-4 animate-spin inline align-[-2px]" /> Loading…
            </div>
          )}
          {!loading && stages.length > 0 && (
            <div style={{ width: "100%", height: 340 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--ds-color-admin-border)"
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: "var(--ds-color-ink-subtle)", fontSize: 11 }}
                    stroke="var(--ds-color-admin-border)"
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: "var(--ds-color-ink-muted)", fontSize: 11 }}
                    stroke="var(--ds-color-admin-border)"
                    width={220}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--ds-color-surface)",
                      border: "1px solid var(--ds-color-admin-border)",
                      borderRadius: "var(--ds-radius-input)",
                      fontSize: 12,
                      color: "var(--ds-color-ink)",
                    }}
                    labelStyle={{ color: "var(--ds-color-ink)", fontWeight: 600 }}
                    itemStyle={{ color: "var(--ds-color-ink-muted)" }}
                    cursor={{ fill: "var(--ds-color-admin-surface-muted)" }}
                  />
                  <Bar
                    dataKey="count"
                    fill="var(--ds-color-accent-strong)"
                    radius={[0, 2, 2, 0]}
                    barSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-4" data-testid="admin-analytics-funnel-stages">
            <div
              className="grid grid-cols-[24px_1fr_140px_100px] gap-3 px-1 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold border-b"
              style={{
                color: "var(--ds-color-ink-subtle)",
                borderColor: "var(--ds-color-admin-border)",
              }}
            >
              <span>#</span>
              <span>Stage</span>
              <span className="text-right">Unique reach</span>
              <span className="text-right">Step %</span>
            </div>
            {stages.map((s, i) => {
              const isLast = i === stages.length - 1;
              return (
                <div
                  key={s.key}
                  className="grid grid-cols-[24px_1fr_140px_100px] gap-3 px-1 py-1.5 items-center text-[12px]"
                  style={{
                    borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <span
                    className="tabular-nums"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate" style={{ color: "var(--ds-color-ink)" }}>
                      {s.label}
                    </div>
                    <div
                      className="text-[10px] font-mono truncate"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      {s.event_name}
                    </div>
                  </div>
                  <span
                    className="text-right tabular-nums font-mono"
                    style={{ color: "var(--ds-color-ink)" }}
                  >
                    {s.count}
                  </span>
                  <span
                    className="text-right tabular-nums font-mono"
                    style={{ color: "var(--ds-color-accent-strong)" }}
                  >
                    {s.step_conversion_pct != null ? `${s.step_conversion_pct}%` : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>

        <BreakdownWidget breakdown={breakdown} loading={loading} />

        <Section
          title="Event volume — top 20"
          icon={ListTree}
          testid="admin-analytics-events-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-analytics-events-table">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">Event</th>
                  <th className="px-4 py-2 text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {summary.slice(0, 20).map((row, idx) => {
                  const isLast = idx === Math.min(summary.length, 20) - 1;
                  return (
                    <tr
                      key={row.event_name}
                      style={{
                        borderBottom: isLast
                          ? "none"
                          : "1px solid var(--ds-color-admin-border)",
                      }}
                    >
                      <td
                        className="px-4 py-1.5 font-mono text-[12px]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.event_name}
                      </td>
                      <td
                        className="px-4 py-1.5 text-right tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {row.count}
                      </td>
                    </tr>
                  );
                })}
                {!loading && summary.length === 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No events yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Stat + Breakdown                                                     */
/* ------------------------------------------------------------------ */


function BreakdownWidget({ breakdown, loading }) {
  const cohorts = breakdown?.cohorts || [];
  const sources = breakdown?.sources || [];
  if (!loading && cohorts.length === 0 && sources.length === 0) return null;

  const windowBadge = breakdown?.window_days ? (
    <span
      className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
      style={{ color: "var(--ds-color-ink-muted)" }}
    >
      Last {breakdown.window_days} days
    </span>
  ) : null;

  return (
    <Section
      title="Cohort / Source breakdown · signups → activations"
      icon={Layers}
      badge={windowBadge}
      testid="admin-analytics-breakdown"
      bodyClassName="p-3"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BreakdownPanel title="By cohort" rows={cohorts} testIdPrefix="cohort" />
        <BreakdownPanel title="By acquisition source" rows={sources} testIdPrefix="source" />
      </div>
    </Section>
  );
}

function BreakdownPanel({ title, rows, testIdPrefix }) {
  const max = Math.max(1, ...rows.map((r) => r.signups));
  return (
    <div
      className="rounded-[var(--ds-radius-card)] p-3"
      data-testid={`admin-analytics-breakdown-${testIdPrefix}`}
      style={{
        background: "var(--ds-color-admin-surface-muted)",
        border: "1px solid var(--ds-color-admin-border)",
      }}
    >
      <div
        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-2"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {title}
      </div>
      {rows.length === 0 && (
        <div
          className="text-[12px] py-2"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          No data in this window.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((r) => {
          const pct = Math.min(100, Math.round((r.signups / max) * 100));
          const actPct = r.signups > 0 ? Math.round((r.activations / r.signups) * 100) : 0;
          const actWidth = Math.min(100, Math.round((r.activations / max) * 100));
          return (
            <div
              key={r.key}
              data-testid={`admin-analytics-breakdown-${testIdPrefix}-row-${r.key}`}
            >
              <div className="flex items-baseline justify-between text-[12px] mb-1 gap-2">
                <span
                  className="font-mono truncate max-w-[60%]"
                  style={{ color: "var(--ds-color-ink)" }}
                >
                  {r.key || "(unknown)"}
                </span>
                <span
                  className="tabular-nums font-mono"
                  style={{ color: "var(--ds-color-ink-muted)" }}
                >
                  {r.signups} signups
                  <span className="ml-2" style={{ color: "var(--ds-color-accent-strong)" }}>
                    {r.activations} act ({r.activation_pct}%)
                  </span>
                </span>
              </div>
              <div
                className="relative h-1.5 overflow-hidden rounded-[var(--ds-radius-input)]"
                style={{ background: "var(--ds-color-surface-inset)" }}
              >
                <div
                  className="absolute inset-y-0 left-0"
                  style={{ width: `${pct}%`, background: "var(--ds-color-accent-strong)" }}
                />
                <div
                  className="absolute inset-y-0 left-0"
                  style={{
                    width: `${actWidth}%`,
                    background: "var(--ds-color-info)",
                    opacity: 0.75,
                  }}
                />
              </div>
              <div
                className="flex items-center gap-3 text-[10px] mt-1 tabular-nums"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                <span className="inline-flex items-center gap-1">
                  <span
                    className="inline-block w-2 h-2 rounded-[1px]"
                    style={{ background: "var(--ds-color-accent-strong)" }}
                  />
                  Signups
                </span>
                <span className="inline-flex items-center gap-1">
                  <span
                    className="inline-block w-2 h-2 rounded-[1px]"
                    style={{ background: "var(--ds-color-info)", opacity: 0.75 }}
                  />
                  Activations · {actPct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
